// Third-party libraries
import React, { useContext, useState, useEffect } from "react";
import {
  Select,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@mui/material";

// Local modules
import { BuildARingContext } from "../../../utils/stateHandlers/contexts";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const AUTO_DEFAULT_TREATMENT_TYPES = new Set([
  "FINISH",
  "PLATING",
  "POLISH",
  "STAMP",
  "MESSAGE",
  "PACKAGE",
  "SETSTONE",
  "ASSEMBLY",
]);

export default function BarTreatments(props) {
  const { barState, barDispatch } = useContext(BuildARingContext);
  const { clear, validationErrors, priceLoaded, setting, shank } = barState;
  const [localTreatment, setLocalTreatment] = useState("None");
  const field = props.field;
  const isShank = props.shank;

  function updateTreatments(treatmentType, event) {
    const rawValue = event.target.value;
    const isClearingSelection =
      rawValue === "None" ||
      (typeof rawValue === "string" && rawValue.trim().length === 0);
    const normalizedValue = isClearingSelection
      ? ""
      : typeof rawValue === "string"
      ? rawValue.trim()
      : rawValue;

    const info = isShank ? shank?.shankInfo : setting?.settingInfo;
    const actionType = isShank
      ? "updateShankTreatment"
      : "updateSettingTreatment";
    const treatments = Array.isArray(info?.treatments) ? info.treatments : [];
    const existingIndex = treatments.findIndex(
      (treatment) => treatment.name === treatmentType
    );

    let updatedTreatments = treatments;

    if (existingIndex !== -1) {
      if (isClearingSelection) {
        updatedTreatments = treatments.filter(
          (_, index) => index !== existingIndex
        );
      } else if (treatments[existingIndex].value !== normalizedValue) {
        updatedTreatments = treatments.map((treatment, index) =>
          index === existingIndex
            ? { ...treatment, value: normalizedValue }
            : treatment
        );
      }
    } else if (!isClearingSelection) {
      updatedTreatments = [
        ...treatments,
        { name: treatmentType, value: normalizedValue },
      ];
    }

    if (updatedTreatments !== treatments) {
      barDispatch({
        type: actionType,
        payload: updatedTreatments,
      });
    }

    setLocalTreatment(isClearingSelection ? " " : normalizedValue);
  }

  useEffect(() => {
    if (clear) {
      barDispatch({ type: "setClear", payload: false });
      barDispatch({
        type: "setValidationErrors",
        payload: [...validationErrors, props.field.name],
      });
      setLocalTreatment("None");
      barDispatch({
        type: "updateFields",
        field: props.field.name,
        payload: "None",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clear]);

  useEffect(() => {
    if (!AUTO_DEFAULT_TREATMENT_TYPES.has(field.type)) {
      return;
    }

    if (!field.values || field.values.length === 0) {
      if (localTreatment !== " ") {
        setLocalTreatment(" ");
      }
      return;
    }

    const treatments = isShank
      ? shank?.shankInfo?.treatments
      : setting?.settingInfo?.treatments;

    if (!Array.isArray(treatments)) {
      if (localTreatment !== " ") {
        setLocalTreatment(" ");
      }
      return;
    }

    const existingTreatment = treatments.find(
      (treatment) => treatment.name === field.type
    );

    if (!existingTreatment) {
      if (localTreatment !== " ") {
        setLocalTreatment(" ");
      }
      return;
    }

    const normalizedExistingValueRaw =
      typeof existingTreatment.value === "string"
        ? existingTreatment.value.trim()
        : existingTreatment.value;

    const hasValidExistingValue =
      normalizedExistingValueRaw !== null &&
      normalizedExistingValueRaw !== undefined &&
      normalizedExistingValueRaw !== "" &&
      field.values.some(
        (option) => option.value === normalizedExistingValueRaw
      );

    if (!hasValidExistingValue) {
      updateTreatments(field.type, { target: { value: "None" } });
      return;
    }

    const normalizedExistingValue =
      typeof normalizedExistingValueRaw === "string"
        ? normalizedExistingValueRaw
        : String(normalizedExistingValueRaw);

    if (localTreatment !== normalizedExistingValue) {
      setLocalTreatment(normalizedExistingValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, isShank, setting, shank]);

  return (
    <Stack key={field.name}>
      <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
        {field.label}
      </Typography>
      <FormControl error={localTreatment === "None" ? true : false}>
        <Select
          key={field.name}
          value={
            localTreatment === null
              ? field.default === ""
                ? "None"
                : field.default
              : localTreatment
          }
          size="small"
          sx={{ width: "100%" }}
          disabled={!priceLoaded}
          onChange={(e) => {
            updateTreatments(field.type, e);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #939598",
                borderRadius: "0",
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              },
            },
          }}
        >
          <MenuItem value="None">
            <em>Select...</em>
          </MenuItem>
          <MenuItem value=" ">None</MenuItem>
          {field.values &&
            field.values.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </Select>
        <FormHelperText>
          {localTreatment === "None" ? "Please Make A Selection" : ""}
        </FormHelperText>
      </FormControl>
    </Stack>
  );
}
